/**
 * SourceStep - Step 2 of Add Budget Dialog: Select investment line or create new
 */

import React, { useMemo } from "react";
import {
  Cube,
  MagnifyingGlass,
  CurrencyEur,
  Tag,
  Warning,
  ArrowUp,
  PlusCircle,
  Spinner,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RenovationLabel } from "shared/types/renovation";
import type { InvestmentItem } from "screens/investment/types/investment";
import { DialogState, formatCurrency } from "./types";

interface SourceStepProps {
  dialogState: DialogState;
  setDialogState: React.Dispatch<React.SetStateAction<DialogState>>;
  labels: RenovationLabel[];
  allInvestmentItems: InvestmentItem[];
  loadingItems: boolean;
  budgetSearch: string;
  setBudgetSearch: (value: string) => void;
  getMatchingItemsForUnits: (unitIds: string[], labelFilterId: number | null) => InvestmentItem[];
  getItemById: (itemId: number) => InvestmentItem | undefined;
}

export const SourceStep: React.FC<SourceStepProps> = ({
  dialogState,
  setDialogState,
  labels,
  loadingItems,
  budgetSearch,
  setBudgetSearch,
  getMatchingItemsForUnits,
  getItemById,
}) => {
  const filteredMatchingItems = useMemo(() => {
    if (!budgetSearch.trim()) return dialogState.matchingItems;
    const search = budgetSearch.toLowerCase().trim();
    return dialogState.matchingItems.filter(
      (item) =>
        item.title.toLowerCase().includes(search) ||
        item.label?.name?.toLowerCase().includes(search)
    );
  }, [dialogState.matchingItems, budgetSearch]);

  const handleLabelFilterChange = (labelId: number | null) => {
    const matching = getMatchingItemsForUnits(dialogState.unitIds, labelId);
    setDialogState((prev) => ({
      ...prev,
      labelFilter: labelId,
      matchingItems: matching,
      selectedItemId: null,
      isNewBudget: false,
    }));
  };

  const handleSourceSelect = (itemId: number | null, isNew: boolean) => {
    if (isNew) {
      setDialogState((prev) => ({
        ...prev,
        selectedItemId: null,
        isNewBudget: true,
        newBudgetLabelId: prev.labelFilter,
        amount: 0,
        overflowOption: null,
        overflowReason: "",
      }));
    } else if (itemId) {
      const item = getItemById(itemId);
      if (item) {
        const remaining = Number(item.estimatedCostExclVat) - Number(item.allocatedBudget || 0);
        setDialogState((prev) => ({
          ...prev,
          selectedItemId: itemId,
          isNewBudget: false,
          newBudgetLabelId: null,
          amount: remaining,
          overflowOption: null,
          overflowReason: "",
        }));
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Selected units */}
      <div className="flex items-center gap-2 rounded bg-neutral-50 p-2 text-sm">
        <Cube className="h-4 w-4 text-moss-primary" />
        <span className="font-medium">
          {dialogState.unitIds.length === 1
            ? dialogState.unitIds[0]
            : `${dialogState.unitIds.length} kostenplaatsen`}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setDialogState((prev) => ({ ...prev, step: "units" }))}
          className="ml-auto text-xs"
        >
          Wijzig
        </Button>
      </div>

      {/* Label filter */}
      <div className="space-y-2">
        <Label className="text-xs text-neutral-500">Filter op categorie:</Label>
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => handleLabelFilterChange(null)}
            className={`rounded-full border px-2 py-1 text-xs transition-colors ${
              dialogState.labelFilter === null
                ? "border-moss-primary bg-moss-primary text-white"
                : "border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-moss-primary"
            }`}
          >
            Alle
          </button>
          {labels.map((label) => (
            <button
              key={label.id}
              type="button"
              onClick={() => handleLabelFilterChange(label.id)}
              className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                dialogState.labelFilter === label.id
                  ? "border-brick-primary bg-brick-primary text-white"
                  : "border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-brick-primary"
              }`}
            >
              {label.name}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input
          type="text"
          value={budgetSearch}
          onChange={(e) => setBudgetSearch(e.target.value)}
          placeholder="Zoek in budgetten..."
          className="pl-9"
        />
      </div>

      {/* Budget list */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Beschikbare budgetten</Label>
          <span className="text-xs text-neutral-500">{filteredMatchingItems.length} gevonden</span>
        </div>

        {loadingItems ? (
          <div className="flex items-center gap-2 text-neutral-500">
            <Spinner className="h-4 w-4 animate-spin" />
            Laden...
          </div>
        ) : (
          <>
            {filteredMatchingItems.length > 0 && (
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {filteredMatchingItems.map((item) => {
                  const total = Number(item.estimatedCostExclVat) || 0;
                  const allocated = Number(item.allocatedBudget) || 0;
                  const remaining = total - allocated;
                  const usagePercent = total > 0 ? (allocated / total) * 100 : 0;
                  const isFullyUsed = remaining <= 0;
                  const isOverUsed = remaining < 0;
                  const isSelected =
                    dialogState.selectedItemId === item.id && !dialogState.isNewBudget;

                  return (
                    <Tooltip key={item.id}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={`w-full rounded-md border p-3 text-left transition-colors ${
                            isSelected
                              ? "border-moss-primary bg-moss-primary text-white"
                              : isFullyUsed
                                ? "border-red-200 bg-red-50 hover:border-red-300 hover:bg-red-100"
                                : "border-neutral-200 bg-white hover:border-moss-primary hover:bg-moss-primary/5"
                          }`}
                          onClick={() => handleSourceSelect(item.id, false)}
                        >
                          <div className="space-y-1">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-sm font-medium">{item.title}</span>
                              {isFullyUsed ? (
                                <span
                                  className={`shrink-0 text-xs font-medium ${
                                    isSelected ? "opacity-80" : "text-red-600"
                                  }`}
                                >
                                  {isOverUsed
                                    ? `>${Math.round(usagePercent)}% toegewezen`
                                    : "100% toegewezen"}
                                </span>
                              ) : (
                                <span
                                  className={`shrink-0 text-xs ${
                                    isSelected ? "opacity-80" : "font-medium text-moss-primary"
                                  }`}
                                >
                                  {formatCurrency(remaining)}
                                </span>
                              )}
                            </div>
                            <div
                              className={`text-xs ${
                                isSelected
                                  ? "opacity-70"
                                  : isFullyUsed
                                    ? "text-red-500"
                                    : "text-neutral-500"
                              }`}
                            >
                              #{item.id} • {formatCurrency(total)} totaal
                              {isFullyUsed && " • Extra budget vereist goedkeuring"}
                            </div>
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="start" className="max-w-xs">
                        <div className="text-xs space-y-1">
                          {item.description && <p>{item.description}</p>}
                          <p className="font-medium">
                            Budget: {formatCurrency(allocated)} / {formatCurrency(total)} toegewezen
                            ({Math.round(usagePercent)}%)
                          </p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            )}

            {dialogState.matchingItems.length === 0 && (
              <div className="py-2 text-sm italic text-neutral-500">
                Geen bestaande investeringslijnen gevonden met deze categorie en kostenplaats.
              </div>
            )}

            {/* Create new button */}
            <div className="border-t pt-2">
              <Button
                type="button"
                variant={dialogState.isNewBudget ? "default" : "outline"}
                className={`h-auto w-full justify-start py-2 ${
                  dialogState.isNewBudget ? "bg-blue-600" : ""
                }`}
                onClick={() => handleSourceSelect(null, true)}
              >
                <CurrencyEur className="mr-2 h-4 w-4" />
                <div className="text-left">
                  <div className="font-medium">Nieuw budget aanmaken</div>
                  <div className="text-xs opacity-80">Maakt een nieuwe investeringslijn aan</div>
                </div>
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Amount and options */}
      {(dialogState.selectedItemId || dialogState.isNewBudget) && (
        <AmountSection
          dialogState={dialogState}
          setDialogState={setDialogState}
          labels={labels}
          getItemById={getItemById}
        />
      )}
    </div>
  );
};

// Extracted sub-component for amount input and overflow handling
const AmountSection: React.FC<{
  dialogState: DialogState;
  setDialogState: React.Dispatch<React.SetStateAction<DialogState>>;
  labels: RenovationLabel[];
  getItemById: (itemId: number) => InvestmentItem | undefined;
}> = ({ dialogState, setDialogState, labels, getItemById }) => {
  const selectedItem = dialogState.selectedItemId
    ? getItemById(dialogState.selectedItemId)
    : undefined;
  const remaining = selectedItem
    ? Number(selectedItem.estimatedCostExclVat) - Number(selectedItem.allocatedBudget || 0)
    : 0;
  const overflowAmount = dialogState.amount - remaining;
  const hasOverflow = overflowAmount > 0 && !dialogState.isNewBudget && selectedItem;

  return (
    <div className="space-y-3 border-t pt-4">
      {/* New budget label selection */}
      {dialogState.isNewBudget && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-sm">Categorie voor nieuwe investeringslijn *</Label>
            <div className="flex flex-wrap gap-1">
              {labels.map((label) => (
                <button
                  key={label.id}
                  type="button"
                  onClick={() => setDialogState((prev) => ({ ...prev, newBudgetLabelId: label.id }))}
                  className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                    dialogState.newBudgetLabelId === label.id
                      ? "border-brick-primary bg-brick-primary text-white"
                      : "border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-brick-primary"
                  }`}
                >
                  <Tag className="mr-1 inline h-3 w-3" />
                  {label.name}
                </button>
              ))}
            </div>
            {!dialogState.newBudgetLabelId && (
              <p className="text-xs text-amber-600">
                Selecteer een categorie voor de nieuwe investeringslijn
              </p>
            )}
          </div>

          {/* Reason for new investment line - audit trail */}
          <div className="space-y-1">
            <Label className="text-sm">Reden voor aanmaak investeringslijn *</Label>
            <Textarea
              value={dialogState.creationReason}
              onChange={(e) =>
                setDialogState((prev) => ({ ...prev, creationReason: e.target.value }))
              }
              placeholder="Waarom wordt deze investeringslijn aangemaakt? (min. 5 tekens)"
              rows={2}
              className="text-sm"
            />
            {dialogState.creationReason.trim().length > 0 &&
              dialogState.creationReason.trim().length < 5 && (
                <p className="text-xs text-amber-600">
                  Minimaal 5 tekens vereist voor de audit trail
                </p>
              )}
          </div>
        </div>
      )}

      {/* Amount input */}
      <div className="space-y-1">
        <Label className="text-sm">Budget *</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">
            €
          </span>
          <Input
            type="number"
            value={dialogState.amount === 0 ? "" : dialogState.amount}
            onChange={(e) => {
              const value = e.target.value;
              const numValue = value === "" ? 0 : parseFloat(value);
              setDialogState((prev) => ({
                ...prev,
                amount: isNaN(numValue) ? 0 : numValue,
                overflowOption: null,
                overflowReason: "",
              }));
            }}
            className="pl-7"
            min={0}
            step={100}
            placeholder="Voer bedrag in..."
          />
        </div>

        {/* Remaining info for existing item */}
        {!dialogState.isNewBudget && selectedItem && (
          <div className={`text-xs ${remaining === 0 ? "text-amber-600" : "text-neutral-500"}`}>
            {remaining === 0
              ? "Budget volledig toegewezen"
              : `Max beschikbaar: ${formatCurrency(remaining)}`}
          </div>
        )}
      </div>

      {/* Overflow handling */}
      {hasOverflow && selectedItem && (
        <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <Warning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-800">
              <span className="font-medium">Extra budget nodig:</span>{" "}
              {formatCurrency(overflowAmount)}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-amber-700">
              Hoe wil je dit oplossen?
            </Label>

            <button
              type="button"
              onClick={() =>
                setDialogState((prev) => ({
                  ...prev,
                  overflowOption: "increase",
                  newBudgetLabelId: null,
                }))
              }
              className={`w-full rounded-md border p-2 text-left transition-colors ${
                dialogState.overflowOption === "increase"
                  ? "border-amber-400 bg-amber-100"
                  : "border-neutral-200 bg-white hover:border-amber-300"
              }`}
            >
              <div className="flex items-center gap-2">
                <ArrowUp
                  className={`h-4 w-4 ${
                    dialogState.overflowOption === "increase"
                      ? "text-amber-600"
                      : "text-neutral-400"
                  }`}
                />
                <div>
                  <div className="text-sm font-medium">Verhoog bestaande investeringslijn</div>
                  <div className="text-xs text-neutral-500">
                    Budget van #{selectedItem.id} wordt verhoogd met {formatCurrency(overflowAmount)}
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() =>
                setDialogState((prev) => ({
                  ...prev,
                  overflowOption: "new_line",
                  overflowReason: "",
                  newBudgetLabelId: selectedItem.label?.id || null,
                }))
              }
              className={`w-full rounded-md border p-2 text-left transition-colors ${
                dialogState.overflowOption === "new_line"
                  ? "border-blue-400 bg-blue-100"
                  : "border-neutral-200 bg-white hover:border-blue-300"
              }`}
            >
              <div className="flex items-center gap-2">
                <PlusCircle
                  className={`h-4 w-4 ${
                    dialogState.overflowOption === "new_line"
                      ? "text-blue-600"
                      : "text-neutral-400"
                  }`}
                />
                <div>
                  <div className="text-sm font-medium">Maak nieuwe investeringslijn</div>
                  <div className="text-xs text-neutral-500">
                    Nieuwe lijn van {formatCurrency(overflowAmount)} voor het extra budget
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Increase reason */}
          {dialogState.overflowOption === "increase" && (
            <div className="space-y-1 border-t border-amber-200 pt-2">
              <Label className="text-xs text-amber-700">Reden voor budgetverhoging *</Label>
              <Textarea
                value={dialogState.overflowReason}
                onChange={(e) =>
                  setDialogState((prev) => ({ ...prev, overflowReason: e.target.value }))
                }
                placeholder="Waarom moet het budget verhoogd worden?"
                rows={2}
                className="bg-white text-sm"
              />
              <p className="text-xs text-amber-600">Dit wordt vastgelegd in de audit trail.</p>
            </div>
          )}

          {/* New line label selection */}
          {dialogState.overflowOption === "new_line" && (
            <div className="space-y-3 border-t border-blue-200 pt-2">
              <div className="space-y-2">
                <Label className="text-xs text-blue-700">
                  Categorie voor nieuwe investeringslijn *
                </Label>
                <div className="flex flex-wrap gap-1">
                  {labels.map((label) => (
                    <button
                      key={label.id}
                      type="button"
                      onClick={() =>
                        setDialogState((prev) => ({ ...prev, newBudgetLabelId: label.id }))
                      }
                      className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                        dialogState.newBudgetLabelId === label.id
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-neutral-200 bg-white text-neutral-600 hover:border-blue-400"
                      }`}
                    >
                      {label.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason for overflow new line - audit trail */}
              <div className="space-y-1">
                <Label className="text-xs text-blue-700">Reden voor nieuwe investeringslijn *</Label>
                <Textarea
                  value={dialogState.overflowCreationReason}
                  onChange={(e) =>
                    setDialogState((prev) => ({ ...prev, overflowCreationReason: e.target.value }))
                  }
                  placeholder="Waarom wordt deze extra investeringslijn aangemaakt? (min. 5 tekens)"
                  rows={2}
                  className="bg-white text-sm"
                />
                {dialogState.overflowCreationReason.trim().length > 0 &&
                  dialogState.overflowCreationReason.trim().length < 5 && (
                    <p className="text-xs text-blue-600">
                      Minimaal 5 tekens vereist voor de audit trail
                    </p>
                  )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SourceStep;
